import { useState } from "react";
import {
  Trees,
  Users,
  CalendarDays,
  Landmark,
  ArrowRight,
} from "lucide-react";

const AREAS = [
  {
    icon: Trees,
    title: "Ambiente e Fauna",
    desc: "Tutela del territorio, prevenzione ambientale, sensibilizzazione, biodiversità e attenzione alla fauna selvatica.",
    keywords: ["Bosco", "Biodiversità", "Prevenzione", "Fauna selvatica"],
  },
  {
    icon: Users,
    title: "Comunità",
    desc: "Supporto alla comunità, cittadinanza attiva, vicinanza alle persone e iniziative sociali per il territorio.",
    keywords: ["Cittadini", "Solidarietà", "Vicinato", "Iniziative sociali"],
  },
  {
    icon: CalendarDays,
    title: "Eventi e Territorio",
    desc: "Organizzazione di eventi, iniziative territoriali e momenti di aggregazione per valorizzare Asciano.",
    keywords: ["Ritrovi", "Iniziative", "Aggregazione", "Valorizzazione"],
  },
  {
    icon: Landmark,
    title: "Identità Ascianese",
    desc: "Valorizzazione della storia, della cultura, delle tradizioni e dell'identità del territorio ascianese.",
    keywords: ["Storia", "Cultura", "Tradizioni", "Identità locale"],
  },
];

export const AreasOfAction = () => {
  const [hovered, setHovered] = useState(null);

  return (
    <section
      id="aree"
      data-testid="areas-section"
      className="relative py-20 sm:py-28 bg-[#FBF9F6] overflow-hidden"
    >
      {/* Linee decorative orizzontali */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-[#E5E1D8]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-[#E5E1D8]" />

      <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header allineato a sinistra, stile editoriale */}
        <div className="mb-14 sm:mb-20 max-w-2xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-10 h-px bg-[#C15C3D]" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-[#C15C3D]">
              Cosa facciamo
            </span>
          </div>
          <h2
            data-testid="areas-title"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-[#1A1A1A] leading-[0.95] tracking-tight"
          >
            Le nostre
            <br />
            <span className="italic font-light text-[#2E4F3B]">
              aree d'azione.
            </span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#5C5C5C] leading-relaxed">
            Quattro ambiti su cui ci impegniamo, ogni giorno, per il bene di
            Asciano.
          </p>
        </div>

        {/* Lista editoriale numerata */}
        <ul className="border-t-2 border-[#1A1A1A]">
          {AREAS.map((a, i) => {
            const Icon = a.icon;
            const isHovered = hovered === i;
            return (
              <li
                key={a.title}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                data-testid={`area-row-${i}`}
                className="group relative border-b border-[#1A1A1A]/15 transition-colors"
              >
                {/* Hover green strip */}
                <div
                  className={`pointer-events-none absolute inset-y-0 left-0 w-1 bg-[#2E4F3B] transition-all duration-500 ${
                    isHovered ? "opacity-100" : "opacity-0"
                  }`}
                />

                <div className="grid grid-cols-12 gap-4 sm:gap-6 py-7 sm:py-10 px-2 sm:px-6">
                  {/* Numero grande */}
                  <div className="col-span-2 sm:col-span-1 flex items-start">
                    <span
                      className={`font-display text-3xl sm:text-5xl font-black tabular-nums leading-none transition-colors ${
                        isHovered ? "text-[#2E4F3B]" : "text-[#1A1A1A]/30"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>

                  {/* Titolo + descrizione */}
                  <div className="col-span-10 sm:col-span-7 flex flex-col">
                    <h3
                      className={`font-display text-2xl sm:text-3xl lg:text-4xl font-black leading-tight tracking-tight transition-colors ${
                        isHovered ? "text-[#2E4F3B]" : "text-[#1A1A1A]"
                      }`}
                    >
                      {a.title}
                    </h3>
                    <p className="mt-3 sm:mt-4 text-base sm:text-lg text-[#5C5C5C] leading-relaxed max-w-xl">
                      {a.desc}
                    </p>

                    {/* Keywords chips */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {a.keywords.map((k) => (
                        <span
                          key={k}
                          className="inline-block text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-[#5C5C5C] bg-white border border-[#E5E1D8] rounded-full px-3 py-1"
                        >
                          {k}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Icona destra */}
                  <div className="col-span-12 sm:col-span-4 flex sm:items-center sm:justify-end mt-2 sm:mt-0">
                    <div
                      className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center transition-all duration-500 ${
                        isHovered
                          ? "bg-[#2E4F3B] text-white -translate-y-1 shadow-lg"
                          : "bg-white border-2 border-[#E5E1D8] text-[#2E4F3B]"
                      }`}
                    >
                      <Icon
                        className="w-7 h-7 sm:w-9 sm:h-9"
                        strokeWidth={1.7}
                      />
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        {/* Footer CTA */}
        <div className="mt-12 sm:mt-14 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <p className="text-base sm:text-lg text-[#5C5C5C] italic">
            Vuoi essere parte di tutto questo?
          </p>
          <a
            href="#volontari"
            data-testid="areas-cta"
            className="inline-flex items-center gap-3 rounded-full bg-[#2E4F3B] hover:bg-[#1f3828] text-white font-bold text-base sm:text-lg px-8 py-4 transition-all hover:-translate-y-0.5 shadow-md self-start sm:self-auto"
          >
            Unisciti a noi
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};
